import Card from '@mui/material/Card';
import useMediaQuery from '@mui/material/useMediaQuery';
import { withStyles } from 'tss-react/mui';
import { supportsPassiveEvents } from 'detect-it';
import React, { useEffect, useRef, useState } from 'react';
import VoteSlider from './VoteSlider';

const styles = theme => ({
  // In-flow variant (internal blog, video posts) — paper card, not a
  // floating Material shadow.
  relative: {
    borderRadius: 14,
    marginTop: '8px',
    marginBottom: '20px',
    boxShadow: 'none',
    border: `1px solid ${
      theme.palette.mode === 'dark' ? '#26382F' : '#E7E0CF'
    }`,
    backgroundColor: theme.palette.mode === 'dark' ? '#15231E' : '#FFFDF6',
    backgroundImage: 'none',
  },
  fixed: {
    borderRadius: 16,
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.22)',
  },
  mobileFixed: {
    borderRadius: 0,
    paddingBottom: 55,
    boxShadow: '0 -6px 30px rgba(0, 0, 0, 0.18)',
  },
});

const StickyVoteSlider = props => {
  const [opacity, setOpacity] = useState(props.isRelative ? 1 : 0);
  const ticking = useRef(false);

  const isMobileView = useMediaQuery('(max-width: 991px)');

  const listenScrollEvent = () => {
    // rAF-throttled: the scrollHeight read forces layout, so do it at most
    // once per frame instead of on every scroll event.
    if (ticking.current) return;
    ticking.current = true;
    window.requestAnimationFrame(() => {
      ticking.current = false;
      // Visible while reading, hidden near the top and over the footer.
      const nearBottom =
        window.scrollY + window.innerHeight >
        document.documentElement.scrollHeight - 400;
      setOpacity(window.scrollY > 450 && !nearBottom ? 1 : 0);
    });
  };

  useEffect(() => {
    if (props.isRelative) return undefined;
    window.addEventListener(
      'scroll',
      listenScrollEvent,
      // better scroll performance: https://github.com/WICG/EventListenerOptions/blob/gh-pages/explainer.md
      supportsPassiveEvents ? { passive: true } : false,
    );
    return () => {
      window.removeEventListener('scroll', listenScrollEvent);
    };
  }, [props.isRelative]);

  const { classes } = props;

  let cardPos = isMobileView ? classes.mobileFixed : classes.fixed;
  if (props.isRelative) cardPos = classes.relative;

  const voteCard = (
    <Card className={cardPos}>
      <VoteSlider
        author={props.author}
        permlink={props.permlink}
        id={props.id}
        title={props.title}
        smiles={props.smiles}
        totalSmiles={props.totalSmiles}
        noOfComments={props.noOfComments}
        depth={props.depth}
        onCommentAdd={props.onCommentAdd}
        noDivider
        mode="sticky"
        showCounts={props.isRelative}
      />
    </Card>
  );

  if (props.isRelative) return <div className="pt-3">{voteCard}</div>;

  return (
    <>
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          right: 0,
          left: 0,
          width: '100%',
          zIndex: '1000',
          opacity,
          pointerEvents: opacity === 0 ? 'none' : undefined,
          transition: 'opacity 0.1s linear',
        }}
        className="safebottom"
      >
        <div className="tw-mx-auto tw-w-full tw-max-w-2xl sm:tw-px-4 sm:tw-pb-4">
          {voteCard}
        </div>
      </div>
    </>
  );
};

export default withStyles(StickyVoteSlider, styles, {
  name: 'StickyVoteSlider',
});
